import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, DataTable, Drawer, EmptyState, Inline, Menu, Panel, Segmented, Skeleton, Stack, t, Text } from '@kanbios/ui';
import type { fleetState } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';
import { prefill, rulesOf } from '../lib/declaration';
import type { Unit } from '../lib/declaration';
import { isSecurityDefect, relativeTime } from '../lib/format';
import { useSession } from '../lib/session';
import { AppSecurityPanels } from './AppSecurityPanels';
import { AttachSiteDialog } from './AttachSiteDialog';
import { IgnoreDialog, IgnoredTable } from './BrickRules';
import { ContainersTable, RunningPanel, SitesTable } from './RunningPanel';
import type { Site } from './RunningPanel';
import { owned } from '../lib/bricks';
import { EnvironmentBadge, notFoundColumn, securityRowLabel, securityRowTone, UrlButton } from './ServerState';
import { DeploymentStatus } from './DeploymentStatus';
import { RescanButton } from './RescanButton';
import { FULL_HEIGHT } from '../lib/tables';

type ServerDetail = fleetState.components['schemas']['ServerDetail'];
type Deployment = ServerDetail['deployments'][number];
type Grouping = 'none' | 'environment' | 'project' | 'both';

/** One row of the applications table: a deployment, or a group of them (environment, project). */
interface Row {
  id: string;
  group: string | null;
  d: Deployment | null;
  bricks: number;
  faults: number;
  /** D118: a security defect (critical vulnerabilities, end of life). */
  security?: boolean;
  children?: Row[];
}

/**
 * D47, then D49: the « Applications » tab of a server. The deployments it
 * carries in one table (application, environment, project, client), grouped by
 * environment and/or project. D51: a row opens the application page (D112: on its
 * Vue d'ensemble, no longer the Briques tab), on that deployment's environment.
 * What no declared deployment takes is behind « Applications non déclarées (N) ».
 */
export function ApplicationsPanel({ server, deployments, defects, canReapply, onReapply, onDeclare }: {
  server: string;
  deployments: Deployment[];
  defects: ServerDetail['applicationDefects'];
  canReapply: boolean;
  onReapply: () => void;
  /** D48: the wizard, for everything undeclared or for one detected group; also who may correct a deployment's rules. */
  onDeclare?: (group?: string) => void;
}) {
  const queryClient = useQueryClient();
  const { enums } = useSession();
  const [attaching, setAttaching] = useState<Site | null>(null);
  const [grouping, setGrouping] = useState<Grouping>('none');
  const navigate = useNavigate();
  const [undeclaredOpen, setUndeclaredOpen] = useState(false);
  // D48: « Ignorer » a group or « Ne plus ignorer » a rule.
  const [ignoring, setIgnoring] = useState<{ name: string; change: Parameters<typeof IgnoreDialog>[0]['change'] } | null>(null);
  const key = ['server-running', server];
  // The same query as the other panels of the page: one reading for all.
  const running = useQuery({
    queryKey: key,
    queryFn: async () => unwrap(await api.fleetState.GET('/servers/{server}/running', { params: { path: { server } } })),
    refetchInterval: 60_000,
  });
  const r = running.data;
  // D96: each deployment's status, the same as on its environment's page (what runs, else the installation's state).
  const environments = useQuery({
    queryKey: ['environments', 'server', server],
    queryFn: async () => unwrap(await api.maintenance.GET('/environments', { params: { query: { server } } })),
    refetchInterval: 60_000,
  });
  const statusOf = useMemo(() => new Map((environments.data?.items ?? []).map(e => [e.deployment, e])), [environments.data]);

  const blocks = useMemo(() => new Map(deployments.map(d => [d.name, owned(d.name, r?.sites ?? [], r?.containers ?? [], r?.units ?? [])])), [deployments, r]);
  // What no declared deployment takes, as the wizard would propose it (D48): one line per application detected.
  // What a person chose to ignore (D48) is not undeclared.
  const undeclared = useMemo(() => (r?.installed ? prefill(r.containers, r.sites, '', r.units ?? [], r.ignored.flatMap(i => i.matches)) : null), [r]);

  const manyProjects = new Set(deployments.map(d => d.project)).size > 1;
  const manyClients = new Set(deployments.map(d => d.clientTitle)).size > 1;
  const environmentLabel = (value: string) => enums.environments.find(e => e.value === value)?.label ?? value;
  const rows = useMemo(() => {
    const leaf = (d: Deployment): Row => {
      const b = blocks.get(d.name);
      const bricks = [...(b?.containers ?? []), ...(b?.units ?? [])];
      // D115: the defects of the deployment (bricks in fault, shared bricks, end of life), the same as the page's
      // alert; a restricted person, who gets no defect list, still sees its bricks in fault.
      const ownFaults = bricks.filter(x => x.brick?.deployment === d.name && x.brick.fault).length;
      // D118: « État » says whether it works; its security defects tint the row instead.
      const own = defects.filter(x => x.deployment === d.name);
      return { id: d.name, group: null, d, bricks: bricks.length, faults: Math.max(ownFaults, own.filter(x => !isSecurityDefect(x.code)).length), security: own.some(x => isSecurityDefect(x.code)) };
    };
    const groupBy = (list: Deployment[], by: (d: Deployment) => [string, string], inner?: (l: Deployment[], prefix: string) => Row[], prefix = ''): Row[] => {
      const groups = new Map<string, { label: string; list: Deployment[] }>();
      for (const d of list) {
        const [id, label] = by(d);
        groups.set(id, { label, list: [...(groups.get(id)?.list ?? []), d] });
      }
      return [...groups.entries()].map(([id, g]) => {
        const children = inner ? inner(g.list, `${prefix}${id}/`) : g.list.map(leaf);
        const leaves = g.list.map(leaf);
        return { id: `group:${prefix}${id}`, group: `${g.label} · ${g.list.length}`, d: null, bricks: leaves.reduce((n, x) => n + x.bricks, 0), faults: leaves.reduce((n, x) => n + x.faults, 0), children };
      });
    };
    const byEnvironment = (d: Deployment): [string, string] => [d.environment, environmentLabel(d.environment)];
    const byProject = (d: Deployment): [string, string] => [d.project, `${d.clientTitle} › ${d.projectTitle}`];
    if (grouping === 'environment') return groupBy(deployments, byEnvironment);
    if (grouping === 'project') return groupBy(deployments, byProject);
    if (grouping === 'both') return groupBy(deployments, byProject, (list, prefix) => groupBy(list, byEnvironment, undefined, prefix));
    return deployments.map(leaf);
  }, [deployments, blocks, grouping, enums, defects]);
  const groupIds = useMemo(() => {
    const ids: string[] = [];
    const walk = (list: Row[]) => list.forEach(x => { if (x.children) { ids.push(x.id); walk(x.children); } });
    walk(rows);
    return ids;
  }, [rows]);

  const alert = defects.length > 0 && (
    <Alert tone="warn" title={t('applications.defects.title', { n: defects.length })}>
      <Stack gap={4}>{defects.map(d => <Text key={`${d.code}:${d.label}`} variant="body-sm">{d.label}</Text>)}</Stack>
    </Alert>
  );
  if (running.isPending) return <Panel title={t('server.tabs.applications')}><Skeleton lines={4} /></Panel>;
  // Not read yet, or nothing installed: the reading panel says why and how to fix it.
  if (!r || !r.installed) {
    return <Stack gap={16}>{alert}<RunningPanel server={server} section="domains" canReapply={canReapply} onReapply={onReapply} /></Stack>;
  }

  const claimedSites = new Set([...blocks.values()].flatMap(b => b.sites));
  const claimedDomains = new Set([...blocks.values()].flatMap(b => [...b.domains]));
  const looseSites = r.sites.filter(s => !claimedSites.has(s));
  // The containers no deployment takes and nobody ignored, with their state and technologies (D46).
  const claimedContainers = new Set([...blocks.values()].flatMap(b => b.containers.map(c => c.name)));
  const ignoredNames = new Set(r.ignored.flatMap(i => i.matches));
  const looseContainers = r.containers.filter(c => !c.brick && !claimedContainers.has(c.name) && !ignoredNames.has(c.name));
  const undeclaredCount = undeclared?.applications.length ?? 0;
  const read = r.readAt ? t('running.readAt', { when: relativeTime(r.readAt) }) : t('running.subtitle');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: key });
  const ignoreGroup = (group: string) => {
    const lines = (undeclared?.units ?? []).filter(u => (u.application ?? u.key) === group);
    setIgnoring({ name: group, change: { add: rulesOf(lines) } });
  };

  return (
    <Stack gap={24}>
      {alert}
      <Panel
        title={t('applications.declared.title')}
        subtitle={read}
        actions={(
          <Inline gap={8}>
            {/* D55: Trivy analyses the server's running images now. */}
            <RescanButton server={server} running={r} />
            <Button variant="secondary" size="sm" icon="travel_explore" count={undeclaredCount} onClick={() => setUndeclaredOpen(true)}>
              {t('applications.undeclared.open')}
            </Button>
          </Inline>
        )}
        flush
      >
        <DataTable
          height={FULL_HEIGHT}
          label={t('applications.declared.table')}
          rows={rows}
          rowKey="id"
          treeColumn="application"
          defaultExpanded={groupIds}
          toolbarStart={(
            <Segmented label={t('applications.group.label')} size="sm" value={grouping} onChange={v => setGrouping(v as Grouping)}
              items={(['none', 'environment', 'project', 'both'] as const).map(id => ({ id, label: t(`applications.group.${id}`) }))} />
          )}
          toolbarDensity={false}
          density="compact"
          // D112: a row opens the application's Vue d'ensemble (what needs an action), on that environment.
          rowTone={(row: Row) => securityRowTone(Boolean(row.security))}
          rowToneLabel={(row: Row) => securityRowLabel(Boolean(row.security))}
          onRowClick={(row: Row) => { if (row.d) navigate(`/applications/${encodeURIComponent(row.d.application)}?env=${encodeURIComponent(row.d.environment)}`); }}
          columns={[
            // What the person looks for first: does each deployment run as declared (D96).
            { key: 'status', label: t('applications.columns.status'), width: 150, sortable: false, pinned: true,
              render: (x: Row) => {
                const e = x.d ? statusOf.get(x.d.name) : undefined;
                return e ? <DeploymentStatus state={e.state} observed={e.observed} size="sm" /> : null;
              } },
            { key: 'application', label: t('applications.columns.application'), strong: true, pinned: true, grow: true, width: 240,
              accessor: (x: Row) => x.group ?? x.d!.application },
            { key: 'environment', label: t('applications.columns.environment'), width: 150, filter: true,
              accessor: (x: Row) => (x.d ? environmentLabel(x.d.environment) : ''),
              render: (x: Row) => (x.d ? <EnvironmentBadge environment={x.d.environment} type={x.d.environmentType} /> : null) },
            // One project (and one client) on the server: already in the page's header, the columns start hidden (column picker).
            { key: 'project', label: t('applications.columns.project'), width: 200, filter: true, hidden: !manyProjects, accessor: (x: Row) => x.d?.projectTitle ?? '' },
            { key: 'client', label: t('applications.columns.client'), width: 200, filter: true, hidden: !manyClients, accessor: (x: Row) => x.d?.clientTitle ?? '' },
            // The public address as one icon; nothing for an internal application.
            { key: 'url', label: t('applications.columns.url'), width: 90, sortable: false, accessor: (x: Row) => x.d?.url ?? '', render: (x: Row) => <UrlButton url={x.d?.url} /> },
            // RG-032-2, D115: a declared address no site of the server serves (was in the overview's table).
            { ...notFoundColumn<{ url?: string | null; foundOnServer?: boolean | null }>(), accessor: (x: Row) => (x.d?.url && x.d.foundOnServer === false ? t('server.deployments.notFound') : ''), render: (x: Row) => (x.d?.url && x.d.foundOnServer === false ? <Badge tone="warn" size="sm">{t('server.deployments.notFound')}</Badge> : null) },
            { key: 'bricks', label: t('applications.columns.bricks'), type: 'number', width: 100, accessor: (x: Row) => x.bricks },
            { key: 'state', label: t('applications.columns.state'), width: 140, accessor: (x: Row) => x.faults,
              render: (x: Row) => (x.faults > 0
                ? <Badge tone="warn" size="sm">{t('applications.state.defects', { n: x.faults })}</Badge>
                : x.d ? <Text variant="body-sm" tone="ink-2" truncate>{t('applications.state.ok')}</Text> : null) },
          ]}
          empty={(
            <EmptyState framed={false} icon="deployed_code" title={t('applications.declared.empty')}
              action={undeclaredCount > 0 ? <Button variant="tonal" size="sm" onClick={() => setUndeclaredOpen(true)}>{t('applications.undeclared.open')}</Button> : undefined}>
              {t('applications.declared.emptyBody')}
            </EmptyState>
          )}
        />
      </Panel>
      {!r.restricted && <RunningPanel server={server} section="offPackage" canReapply={canReapply} onReapply={onReapply} />}

      {/* What the agent reads and no declared deployment takes. */}
      <Drawer
        open={undeclaredOpen}
        onClose={() => setUndeclaredOpen(false)}
        size="xl"
        title={t('applications.undeclared.title')}
        subtitle={`${t('applications.undeclared.subtitle')} · ${read}`}
        footer={onDeclare && undeclaredCount > 0 ? <Button icon="add" onClick={() => onDeclare()}>{t('declare.action')}</Button> : undefined}
      >
        <Stack gap={16}>
          {undeclared && undeclared.units.length > 0 && (
            <UndeclaredTable units={undeclared.units} environmentLabel={environmentLabel}
              onDeclare={onDeclare ? group => onDeclare(group) : undefined} onIgnore={r.canAttach ? ignoreGroup : undefined} />
          )}
          {looseContainers.length > 0 && <ContainersTable label={t('applications.loose.containers')} containers={looseContainers} />}
          {looseSites.length > 0 && <SitesTable label={t('applications.loose.sites')} sites={looseSites} wordpress={r.wordpress} onAttach={r.canAttach ? setAttaching : undefined} />}
          {!r.restricted && <AppSecurityPanels server={server} only={{ wordpress: i => !i.domains.some(x => claimedDomains.has(x)), php: true }} />}
          {undeclaredCount === 0 && looseSites.length === 0 && (
            <EmptyState framed={false} icon="task_alt" title={t('applications.loose.empty')} />
          )}
          {/* D48: the groups nobody will declare, written to the catalogue; « Ne plus ignorer » brings them back. */}
          {r.ignored.length > 0 && (
            <Panel title={t('bricks.ignored.title')} subtitle={t('bricks.ignored.subtitle')} flush>
              <IgnoredTable rules={r.ignored} onUnignore={r.canAttach ? rule => setIgnoring({ name: rule.rule, change: { remove: [rule.rule] } }) : undefined} />
            </Panel>
          )}
        </Stack>
      </Drawer>
      {ignoring && (
        <IgnoreDialog server={server} name={ignoring.name} change={ignoring.change} onClose={() => setIgnoring(null)} onOpened={() => { setIgnoring(null); refresh(); }} />
      )}
      {attaching && (
        <AttachSiteDialog
          server={server}
          site={attaching}
          onClose={() => setAttaching(null)}
          onOpened={() => { setAttaching(null); void queryClient.invalidateQueries({ queryKey: key }); }}
        />
      )}
    </Stack>
  );
}

interface UndeclaredRow { id: string; name: string; environment: string | null; kind: Unit['kind'] | null; detail: string; site: string; children?: UndeclaredRow[] }

/**
 * The applications detected and not declared, each with what runs for it (containers, services, cron jobs).
 * D48: each one is declared on its own (the wizard with its lines only) or ignored.
 */
function UndeclaredTable({ units, environmentLabel, onDeclare, onIgnore }: {
  units: Unit[]; environmentLabel: (value: string) => string; onDeclare?: (group: string) => void; onIgnore?: (group: string) => void;
}) {
  const rows: UndeclaredRow[] = [];
  for (const u of units) {
    const application = u.application ?? u.key;
    let parent = rows.find(x => x.id === `app:${application}`);
    if (!parent) rows.push(parent = { id: `app:${application}`, name: application, environment: null, kind: null, detail: '', site: '', children: [] });
    parent.children!.push({ id: u.key, name: u.containers.length > 1 ? `${u.key} ×${u.containers.length}` : u.containers[0] ?? u.key, environment: u.environment, kind: u.kind, detail: u.image, site: u.domains[0] ?? '' });
  }
  for (const p of rows) {
    const environments = [...new Set(p.children!.map(c => c.environment).filter(Boolean))] as string[];
    p.environment = environments.length === 1 ? environments[0]! : null;
  }
  return (
    <DataTable
      height={FULL_HEIGHT}
      label={t('applications.undeclared.table')}
      rows={rows}
      rowKey="id"
      treeColumn="name"
      defaultExpanded={rows.map(x => x.id)}
      toolbar={false}
      density="compact"
      columns={[
        { key: 'name', label: t('applications.undeclared.columns.name'), mono: true, pinned: true, grow: true, width: 260,
          render: (x: UndeclaredRow) => <Text variant={x.kind ? 'code' : 'body-sm'} strong={!x.kind} truncate>{x.name}</Text> },
        { key: 'environment', label: t('applications.columns.environment'), width: 140,
          accessor: (x: UndeclaredRow) => (x.environment ? environmentLabel(x.environment) : ''),
          render: (x: UndeclaredRow) => (x.environment ? <EnvironmentBadge environment={x.environment} /> : x.kind ? <Text variant="body-sm" tone="mute" truncate>—</Text> : null) },
        { key: 'kind', label: t('applications.undeclared.columns.kind'), width: 150,
          accessor: (x: UndeclaredRow) => (x.kind ? (x.kind === 'container' ? t('applications.undeclared.container') : t(`running.units.kinds.${x.kind}`)) : '') },
        { key: 'detail', label: t('applications.undeclared.columns.detail'), mono: true, width: 220 },
        { key: 'site', label: t('applications.undeclared.columns.site'), mono: true, width: 180 },
        ...(onDeclare || onIgnore ? [{
          key: 'actions', label: '', width: 64, sortable: false, pinned: 'right' as const,
          render: (x: UndeclaredRow) => (x.kind ? null : (
            <Menu label={t('applications.undeclared.actionsOf', { name: x.name })} align="end" items={[
              ...(onDeclare ? [{ label: t('applications.undeclared.declare'), icon: 'add', onSelect: () => onDeclare(x.name) }] : []),
              ...(onIgnore ? [{ label: t('applications.undeclared.ignore'), icon: 'visibility_off', onSelect: () => onIgnore(x.name) }] : []),
            ]} />
          )),
        }] : []),
      ]}
      empty={<Inline gap={8}><Text variant="body-sm" tone="mute">{t('applications.loose.empty')}</Text></Inline>}
    />
  );
}
